import React, { useState, useMemo } from 'react';
import {useDispatch} from "react-redux";
import Button from '@mui/material/Button';
import {CloudSyncRounded, CloseRounded, UploadFileRounded, DoubleArrowRounded} from "@mui/icons-material";
import {Tree, Card, Typography, Row, Layout, Modal, Input, notification, Divider, Statistic} from 'antd';
import moment from "moment";
import FileIcon from "../../Components/Files/FileIcon";
import { DoubleLine } from "../../Components/Labels";
import {renameFile, renameFolder, deleteResources} from "../../Api/filesApi";
import {formatBytes, transformResourcesToTreeData} from "./fileUtils";
import {useFileValidation} from '../../Hooks/useFileValidation';
import TableSearch from "../../Components/TableSearch";
import ImageGrid from "./ImageGrid";
import {IMAGE_EXTENSIONS} from "./constants";
const { DirectoryTree } = Tree;

const filterResourcesByExtensions = (resources, extensions) => {
    const extensionSet = new Set(extensions.map(ext => ext.toLowerCase()));
    return resources.filter(resource => extensionSet.has(resource.path.split('.').pop().toLowerCase()));
};


const FileExplorer = ({ resources, handleSelect, maxSelect=null, whitelistExtensions=null,
                      onUpload}) => {
  const dispatch = useDispatch();
  const { validateSelection } = useFileValidation(maxSelect, whitelistExtensions);

  const [selectedPath, setSelectedPath] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newName, setNewName] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [viewMode, setViewMode] = useState('explorer'); // 'explorer' or 'grid'
  const imageResources = useMemo(() => filterResourcesByExtensions(resources, IMAGE_EXTENSIONS), [resources]);



  const imageUrls = imageResources.map(res => res.public_url);


  const showRenameModal = (path) => {
    setCurrentPath(path);
    setIsModalVisible(true);
  };

  const handleRename = (newName) => {
    const newPath = `${currentPath.substring(0, currentPath.lastIndexOf('/') + 1)}${newName}`;
    if (currentPath.endsWith('/')) {
      dispatch(renameFolder({ oldPath: currentPath, newPath }));
    } else {
      dispatch(renameFile({ oldPath: currentPath, newPath }));
    }
    setIsModalVisible(false);
  };

  const inRelativePath = (resourcePath)=>{
    const relpath = resourcePath.split('/').slice(2).join('/');
    return (relpath.startsWith(selectedPath))
  }

  const handleDelete = (paths) => {
    dispatch(deleteResources({ paths }));
  };

  const onSelect = (selectedKeys, info) => {
    if (!info.node.isLeaf) { // Ensure action only on folders
      setSelectedPath(info.node.key);
    }
  };

  const onTableRowSelect = (selectedKeys, selectedRows) => {
    const errors = validateSelection(selectedRows.map(row => row.path));
    if (errors.length > 0) {
      notification.error({
        message: 'Validation Error',
        description: errors.join(' '),
        duration: 2,
      });
      return;
    }
    setSelectedRowKeys(selectedKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onTableRowSelect,
  };


  let columns = [
    {
      title: 'Name',
      dataIndex: 'path',
      key: 'path',
      render: (path, record) => (
        <>
          <DoubleLine
            title={path.split('/').pop().split('.')[0]}
            text={moment(record.created_time).format('MMMM Do YYYY, h:mm:ss a')}
          />
          <Typography.Link onClick={() => showRenameModal(path)}>Rename</Typography.Link>
        </>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (size) => formatBytes(size),
    },
    {
      title: 'Type',
      dataIndex: 'content_type',
      key: 'content_type',
      render: (type, record) => <FileIcon path={record.path} />
    },
        {
          title: 'Updated',
          dataIndex: 'updated_time',
          key: 'updated_time',
          render: (dateString) => <Typography.Text className={'gold-text'}>{moment(dateString).format('MMMM Do YYYY, h:mm:ss a')}</Typography.Text>
        },
        {
          title: 'Public URL',
          dataIndex: 'public_url',
          key: 'public_url',
          render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : null,
        }
  ];


  const treeData = transformResourcesToTreeData(resources);
  const filteredResources = selectedPath ? resources.filter(resource => inRelativePath(resource.path)) : resources;


  console.log(selectedPath)
  return (
    <Layout>

      {viewMode==='explorer' ? (
          <>
            <Layout.Sider className={'full left white'} width={300}>
              <div className={'p-3'}>
                <Typography.Title level={5}>
                  Folder Directory
                </Typography.Title>
                <DirectoryTree style={{width: 250, padding: 0}} onSelect={onSelect} treeData={treeData} />

              </div>
            </Layout.Sider>
            <Layout.Content>
              <Row>
                {handleSelect && (
                      <Button
                          variant={selectedRowKeys.length > 0 ? 'contained' : 'outlined'}
                          color={selectedRowKeys.length > 0 ? 'primary' : 'error'}
                          endIcon={selectedRowKeys.length > 0 ? <DoubleArrowRounded/> : <CloseRounded/>}
                          onClick={() => {
                            if (selectedRowKeys.length > 0) {
                              handleSelect(selectedRowKeys);
                            } else {
                              setIsModalVisible(false)
                            }
                          }}
                      >
                        {selectedRowKeys.length > 0 ? 'Continue With Selected' : "Close and Exit"}
                      </Button>
                )}
                <Button onClick={()=>setViewMode('grid')}>Image Grid</Button>
              </Row>

              <Row justify={'space-between'} align={'middle'} className={'pt-3'} style={{maxWidth: '80%', margin: 'auto'}}>
                  <Statistic
                      title="Total Files"
                      value={`${resources.length}`}
                      prefix={<CloudSyncRounded />}
                  >
                  </Statistic>
                  <Button
                        startIcon={<UploadFileRounded/>}
                        variant={'contained'}
                        onClick={onUpload}
                    >
                      Manage Uploads
                  </Button>
              </Row>
              <Divider style={{borderBottom: '1px solid #ddd'}} />
              <Card bordered={false}>
                <TableSearch
                    columns={columns}
                    dataSource={filteredResources}
                    rowKey="path"
                    rowSelection={rowSelection}
                    scroll={{y: '90vh'}}
                />
              </Card>
            </Layout.Content>
          </>
      ) : (
          <Layout.Content>
            <Row>
              <Button onClick={()=>setViewMode('explorer')}>Back to Explorer</Button>
            </Row>
            <ImageGrid imageUrls={imageUrls}/>
          </Layout.Content>
      )}


      <Modal
          title="Rename File/Folder"
          open={isModalVisible}
          onOk={() => handleRename(newName)}
          onCancel={() => setIsModalVisible(false)}
        >
          <Input placeholder="Enter new name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </Modal>
    </Layout>
  );
};

export default FileExplorer;
